/**
 * Admin Audit Logs Page
 */
import { useLanguage } from "@/context/AppContext";
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ClipboardCheck, ChevronLeft, ChevronRight, RefreshCw, Loader2, User, Shield, FileText, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminAuditLogs() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchLogs();
  }, [page]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/audit-logs`, { 
        params: { page, limit: 20 },
        withCredentials: true 
      });
      setLogs(res.data.logs || []);
      setTotalPages(res.data.pages || 1);
    } catch (err) {
      toast.error('Fehler beim Laden der Audit Logs');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-CH', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getActionBadge = (action) => {
    const badges = {
      'login': { color: 'bg-green-100 text-green-800', icon: User, label: 'Login' },
      'logout': { color: 'bg-gray-100 text-gray-800', icon: User, label: 'Logout' },
      'register': { color: 'bg-blue-100 text-blue-800', icon: User, label: 'Registrierung' },
      'password_change': { color: 'bg-yellow-100 text-yellow-800', icon: Shield, label: 'Passwort geändert' },
      'profile_update': { color: 'bg-purple-100 text-purple-800', icon: FileText, label: 'Profil aktualisiert' },
      'backup_created': { color: 'bg-green-100 text-green-800', icon: Shield, label: 'Backup erstellt' },
      'user_approved': { color: 'bg-green-100 text-green-800', icon: User, label: 'Benutzer genehmigt' },
      'user_rejected': { color: 'bg-red-100 text-red-800', icon: AlertTriangle, label: 'Benutzer abgelehnt' },
      'user_deleted': { color: 'bg-red-100 text-red-800', icon: AlertTriangle, label: 'Benutzer gelöscht' },
    };
    const badge = badges[action] || { color: 'bg-gray-100 text-gray-800', icon: FileText, label: action };
    const Icon = badge.icon;
    return (
      <Badge className={`${badge.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={() => navigate('/admin')} className="rounded-full">
          <ChevronLeft className="w-4 h-4 mr-1" />Zurück
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{t('adminPageAuditLogs')}</h1>
          <p className="text-gray-500">Alle Systemaktivitäten überwachen</p>
        </div>
        <Button variant="outline" onClick={fetchLogs}>
          <RefreshCw className="w-4 h-4 mr-2" />Aktualisieren
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5" />
            Aktivitätsprotokoll ({logs.length} Einträge)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#0044CC]" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <ClipboardCheck className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Keine Audit Logs vorhanden</p>
              <p className="text-sm mt-2">Aktivitäten werden hier protokolliert</p>
            </div>
          ) : (
            <div className="space-y-3">
              {logs.map((log, index) => (
                <div key={index} className="border rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      {getActionBadge(log.action)}
                      <div>
                        <p className="font-medium text-sm">{log.user_email || log.user_id || 'System'}</p>
                        {log.details && (
                          <p className="text-xs text-gray-500">
                            {JSON.stringify(log.details).slice(0, 100)}
                          </p>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-gray-400 whitespace-nowrap">
                      {formatDate(log.created_at)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="flex items-center px-4 text-sm">Seite {page} von {totalPages}</span>
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
